import React, { useState, useEffect } from "react";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from "./ui/dialog";
import { Paperclip, Image, Video, FileText, Headphones, X } from "lucide-react";
import { toast } from "react-toastify";

const { ipcRenderer } = window.require("electron");

const MediaSelectionModal = ({ isOpen, onClose, chatId }) => {
	const [selectedFile, setSelectedFile] = useState(null);
	const [preview, setPreview] = useState(null);
	const [isLoading, setIsLoading] = useState(false);

	const mediaTypes = [
		{
			id: "image",
			label: "Imagem",
			icon: Image,
			accept: ".jpg,.jpeg,.png,.gif,.webp",
			color: "text-blue-400",
			bgColor: "bg-blue-400/10",
			borderColor: "border-blue-400/30",
		},
		{
			id: "video",
			label: "Vídeo",
			icon: Video,
			accept: ".mp4,.avi,.mov,.wmv,.flv,.webm",
			color: "text-red-400",
			bgColor: "bg-red-400/10",
			borderColor: "border-red-400/30",
		},
		{
			id: "audio",
			label: "Áudio",
			icon: Headphones,
			accept: ".mp3,.ogg,.opus,.m4a,.wav,.aac,.flac,.wma",
			color: "text-purple-400",
			bgColor: "bg-purple-400/10",
			borderColor: "border-purple-400/30",
		},
		{
			id: "document",
			label: "Documento",
			icon: FileText,
			accept: ".pdf,.doc,.docx,.txt,.rtf",
			color: "text-green-400",
			bgColor: "bg-green-400/10",
			borderColor: "border-green-400/30",
		},
	];

	const handleFileSelect = async (mediaType) => {
		try {
			const result = await ipcRenderer.invoke("select-media-file", {
				mediaType: mediaType.id
			});

			if (result && result.success && result.filePath) {
				const filePath = result.filePath;
				setSelectedFile({ path: filePath, type: mediaType.id });

				if (mediaType.id === "image") {
					// Usar URL.createObjectURL para preview local seguro
					try {
						const fs = window.require('fs');
						const buffer = fs.readFileSync(filePath);
						const blob = new Blob([buffer]);
						const previewUrl = URL.createObjectURL(blob);
						setPreview(previewUrl);
					} catch (error) {
						console.error('Erro ao criar preview:', error);
						setPreview(null);
					}
				} else {
					setPreview(null);
				}
			} else if (result && !result.success) {
				if (result.error !== 'Seleção cancelada pelo usuário') {
					toast.error(result.error || "Erro ao selecionar arquivo");
				}
			}
		} catch (error) {
			console.error("Erro ao selecionar arquivo:", error);
			toast.error("Erro ao selecionar arquivo");
		}
	};

	const handleSendMedia = async () => {
		if (!selectedFile || !chatId) return;

		setIsLoading(true);
		try {
			const instanceId = localStorage.getItem('currentInstanceId') || 'default';
			const result = await ipcRenderer.invoke("send-media-message", {
				instanceId,
				chatId: chatId,
				filePath: selectedFile.path,
				mediaType: selectedFile.type
			});

			if (result && result.success) {
				toast.success("Mídia enviada com sucesso!");
				handleClose();
			} else {
				toast.error(result?.error || "Erro ao enviar mídia");
			}
		} catch (error) {
			console.error("Erro ao enviar mídia:", error);
			toast.error("Erro ao enviar mídia: " + error.message);
		} finally {
			setIsLoading(false);
		}
	};

	const handleClose = () => {
		// Limpar URL do preview se existir
		if (preview && preview.startsWith('blob:')) {
			URL.revokeObjectURL(preview);
		}
		setSelectedFile(null);
		setPreview(null);
		setIsLoading(false);
		onClose();
	};

	// Limpar URL do preview quando o componente for desmontado
	useEffect(() => {
		return () => {
			if (preview && preview.startsWith('blob:')) {
				URL.revokeObjectURL(preview);
			}
		};
	}, [preview]);

	return (
		<Dialog open={isOpen} onOpenChange={handleClose}>
			<DialogContent className="max-w-[600px] bg-gradient-to-br from-dashboardBg/95 to-darkGradient/95 backdrop-blur-md border border-primaryColor/20 shadow-xl">
				<DialogHeader className="border-b border-primaryColor/10 pb-4">
					<DialogTitle className="text-xl font-bold text-left flex items-center gap-2 text-textOnDarkStrong">
						<Paperclip className="h-5 w-5" />
						Selecionar Arquivo
					</DialogTitle>
				</DialogHeader>

				<div className="space-y-6">
					{!selectedFile ? (
						<>
							<p className="text-textMuted text-center">
								Escolha o tipo de arquivo que deseja enviar:
							</p>
							<div className="grid grid-cols-1 gap-4">
								{mediaTypes.map((mediaType) => {
									const IconComponent = mediaType.icon;
									return (
										<button
											key={mediaType.id}
											onClick={() => handleFileSelect(mediaType)}
											className={`flex items-center gap-4 p-4 rounded-lg border-2 transition-all duration-200 hover:scale-[1.02] ${mediaType.bgColor} ${mediaType.borderColor} hover:border-opacity-50`}
										>
											<div className={`p-3 rounded-full ${mediaType.bgColor}`}>
												<IconComponent className={`h-6 w-6 ${mediaType.color}`} />
											</div>
											<div className="text-left">
												<h3 className="font-semibold text-textOnDarkStrong">
													{mediaType.label}
												</h3>
												<p className="text-sm text-textMuted">
													{mediaType.accept.replace(/\./g, "").toUpperCase()}
												</p>
											</div>
										</button>
									);
								})}
							</div>
						</>
					) : (
						<>
							<div className="space-y-4">
								<div className="flex items-center justify-between">
									<h3 className="font-semibold text-textOnDarkStrong">
										Arquivo Selecionado:
									</h3>
									<button
										onClick={() => {
									// Limpar URL do preview se existir
									if (preview && preview.startsWith('blob:')) {
										URL.revokeObjectURL(preview);
									}
									setSelectedFile(null);
									setPreview(null);
								}}
										className="text-textMuted hover:text-textOnDarkStrong transition-colors"
									>
										<X className="h-4 w-4" />
									</button>
								</div>

								<div className="p-4 rounded-lg bg-slateQuaternary/30 border border-primaryColor/10">
									<p className="text-sm text-textMuted break-all">
										{selectedFile.path.split("/").pop()}
									</p>
								</div>

								{preview && (
									<div className="flex justify-center">
										<img
											src={preview}
											alt="Preview"
											className="max-w-full max-h-64 rounded-lg border border-primaryColor/20"
										/>
									</div>
								)}
							</div>

							<div className="flex gap-3 justify-end">
								<button
									onClick={handleClose}
									className="px-4 py-2 rounded-lg border border-primaryColor/20 text-textMuted hover:text-textOnDarkStrong hover:border-primaryColor/40 transition-all"
									disabled={isLoading}
								>
									Cancelar
								</button>
								<button
									onClick={handleSendMedia}
									className="px-4 py-2 rounded-lg bg-gradient-to-r from-primaryDark/80 to-dashboardAccent/80 text-white hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
									disabled={isLoading}
								>
									{isLoading ? (
										<div className="flex items-center gap-2">
											<div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
											Enviando...
										</div>
									) : (
										"Enviar"
									)}
								</button>
							</div>
						</>
					)}
				</div>
			</DialogContent>
		</Dialog>
	);
};

export default MediaSelectionModal;